import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { getStrongestApplicationStatus } from 'common/householdHelpers';
import {
  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_LISTING_ARCHIVED_REASON_DENIED,
} from 'common/householdConstants';
import { isApplicationPastStatus } from 'common/householdApplication/applicationHelpers';

import PartnerActions from 'client/actions/Partner';

import SrText from 'client/components/SrText';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrPartnerHouseholdPendingApprovalCard from 'client/components/SrPartnerHouseholdPendingApprovalCard';

const applicationsOptions = [
  {
    label: 'In Review',
    value: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  },
  {
    label: 'Archived',
    value: HOUSEHOLD_LISTING_STATUS_ARCHIVED,
  },
  {
    label: 'Denied',
    value: null,
  },
];

const SrPartnerListingPageTenantScreeningHouseholdsApplying = ({
  partner,
  householdsApplying,
  appUrl,
  history,
  dispatch,
  currentUser,
  listingId,
}) => {
  const [optionSelected, setOptionSelected] = useState(HOUSEHOLD_APPLICATION_STATUS_ACCEPTED);
  const {
    nextQualifiedHousehold,
    pendingHouseholds,
    pendingHouseholdsCount,
    deniedHouseholds,
    deniedHouseholdsCount,
  } = householdsApplying;

  if (!nextQualifiedHousehold && !pendingHouseholdsCount && !deniedHouseholdsCount) {
    return null;
  }

  const householdsInReview = [nextQualifiedHousehold, ...pendingHouseholds].filter(
    (household) => !!household,
  );
  const householdsDenied = deniedHouseholds.filter((household) => {
    return (
      !optionSelected &&
      household.householdListing?.cancelReason === HOUSEHOLD_LISTING_ARCHIVED_REASON_DENIED
    );
  });
  const householdsArchived = deniedHouseholds.filter((household) => {
    return (
      optionSelected === HOUSEHOLD_LISTING_STATUS_ARCHIVED &&
      household.householdListing?.cancelReason &&
      household.householdListing.cancelReason !== HOUSEHOLD_LISTING_ARCHIVED_REASON_DENIED
    );
  });
  const households =
    optionSelected === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED
      ? householdsInReview
      : optionSelected === HOUSEHOLD_LISTING_STATUS_ARCHIVED
      ? householdsArchived
      : householdsDenied;

  const householdAcceptedIndex = householdsInReview.findIndex((household) =>
    isApplicationPastStatus(
      {
        status: getStrongestApplicationStatus({
          householdApplications: household.householdApplications,
        }),
      },
      HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
    ),
  );
  const onHouseholdCardChange = async (setTabView) => {
    await dispatch(PartnerActions.getHouseholdsApplyingToListing, {
      listingId: listingId,
      forceRefresh: true,
      includeAllHouseholdDataToChecklist: true,
    });

    if (setTabView) {
      setOptionSelected(setTabView);
    }
  };

  return (
    <>
      <SrPartnerSection
        title="Completed Applications"
        headerRow={
          <div className="flex" style={{ lineHeight: '40px' }}>
            {applicationsOptions.map(({ label, value }) => {
              const isActive = value === optionSelected;
              return (
                <div key={value} style={{ margin: '0 0 0 25px' }}>
                  <SrText
                    size="large"
                    weight="medium"
                    color="blueGreycliff"
                    cursor={isActive ? null : 'pointer'}
                    decoration={isActive ? 'underline-yellow' : null}
                    font="greycliff"
                    opacity={isActive ? null : 0.5}
                    onClick={isActive ? null : () => setOptionSelected(value)}
                    className="partnerDataTableFilterOption"
                  >
                    {label}
                  </SrText>
                </div>
              );
            })}
          </div>
        }
      >
        <SrPartnerList>
          {households.map((household, index) => {
            let disableApproveApplicationButton = false;
            const appIsDenied = optionSelected !== HOUSEHOLD_APPLICATION_STATUS_ACCEPTED;
            const inReviewText = householdAcceptedIndex === index ? 'Approved App' : 'Application';
            const deniedText =
              optionSelected === HOUSEHOLD_LISTING_STATUS_ARCHIVED ? 'Archived App' : 'Denied App';
            const title = `${!appIsDenied ? inReviewText : deniedText} #${household.id}`;

            if (householdAcceptedIndex >= 0) {
              if (appIsDenied || householdAcceptedIndex !== index) {
                disableApproveApplicationButton = true;
              }
            }

            return (
              <SrPartnerHouseholdPendingApprovalCard
                alwaysDenyButton
                key={household.id}
                disableApproveApplicationButton={disableApproveApplicationButton}
                title={title}
                household={household}
                concession={household.concession}
                moveInDate={household.moveInDate}
                householdListing={household.householdListing}
                listing={household.listing}
                tenantScreeningCriteria={household.tenantScreeningCriteria}
                renters={household.renters}
                householdApplications={household.householdApplications}
                householdGuarantors={household.householdGuarantors}
                guarantorApplications={household.guarantorApplications}
                tenantAlertReports={household.tenantAlertReports}
                breedRestrictions={household.breedRestrictions}
                partner={partner}
                history={history}
                currentUser={currentUser}
                appUrl={appUrl}
                onApprove={() => onHouseholdCardChange(HOUSEHOLD_APPLICATION_STATUS_ACCEPTED)}
                onDeny={onHouseholdCardChange}
                leaseStartDate={household.leaseStartDate}
                pendingLandlordDate={household.pendingLandlordDate}
                hasMoreActiveApps={household.activeApps > 0}
                hideDenyButton={appIsDenied}
                applicationIsDenied={appIsDenied}
                onUnArchive={onHouseholdCardChange}
              />
            );
          })}
        </SrPartnerList>
      </SrPartnerSection>
    </>
  );
};

SrPartnerListingPageTenantScreeningHouseholdsApplying.propTypes = {
  partner: PropTypes.object.isRequired,
  householdsApplying: PropTypes.object.isRequired,
  appUrl: PropTypes.string.isRequired,
  history: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  currentUser: PropTypes.object.isRequired,
  listingId: PropTypes.number,
};

SrPartnerListingPageTenantScreeningHouseholdsApplying.displayName =
  'SrPartnerListingPageTenantScreeningHouseholdsApplying';

export default SrPartnerListingPageTenantScreeningHouseholdsApplying;
